/**
 * API-клиент коммуникаций/аналитики colver (`/workspaces/{ws}/...`).
 *
 * Реальный режим бьёт в workspace-scoped эндпоинты по контракту
 * `docs/BACKEND_CONTRACTS.md`; mock-режим (по умолчанию) отдаёт данные из
 * `mock.ts`/`mock-feed.ts`. База — как у `shared/api/workspaces`:
 * `VITE_MEEREC_BASE` (dev напрямую в meerec — '/api/user/v1'; прод через
 * Gatekeeper — '/api/user/meerec/v1'). Auth в dev — bcf tests-wrapper
 * (заголовок `X-Tests-User-Sid`).
 */
import axios from 'axios';
import {readStoredSid} from '@api/auth';
import {readActiveWorkspaceId} from '@api/workspaces/lib/storage';
import type {Communication} from '@entities/communication/model/types';
import type {Employee} from '@entities/employee/model/types';
import type {
  AnalyticsRequest,
  CommunicationRecord,
  CommunicationResultResponse,
  CommunicationStatusResponse,
  CommunicationsFilter,
  CommunicationsListRequest,
  DashboardResponse,
  FeedRequest,
  FeedResponse,
  KpiSnapshot,
  ManagerBrief,
  MinutesRequest,
  MinutesResponse,
  QueueRecommendation,
  QueuesResponse,
} from './types';
import type {Period} from '@lib/atoms';
import {
  MOCK_COMMUNICATIONS,
  MOCK_DETAILED_CALL,
  MOCK_EMPLOYEES,
  MOCK_KPI,
  MOCK_QUEUE_ITEMS,
} from './mock';
import {
  adaptFeedRow,
  CALL_STATUS_TO_API,
  type BackendCommunication,
} from './adapter';
import {extractDurationSeconds} from './duration';
import {mockFeedResponse} from './mock-feed';
import {mockMinutesResponse} from './mock-minutes';
import {
  mockGenerateManagerRecommendation,
  mockManagerBrief,
} from './mock-manager-brief';
import {
  employeesFromCommunications,
  employeeFromCommunications,
  kpiFromCommunications,
  queueFromCommunications,
} from './aggregations';
import {IS_MOCK} from '../mock-mode';

const http = axios.create({
  baseURL: import.meta.env.VITE_MEEREC_BASE ?? '/api/user/meerec/v1',
});

const fallbackDevSid = import.meta.env.VITE_DEV_USER_SID;
http.interceptors.request.use((config) => {
  const sid = readStoredSid() ?? fallbackDevSid;
  if (sid) config.headers.set('X-Tests-User-Sid', sid);
  return config;
});

const delay = (ms = 200) => new Promise((r) => setTimeout(r, ms));

// ─── Реальные colver-эндпоинты (workspace-scoped) ───────────────────────────

/** Лента коммуникаций с MQL-фильтром/сортировкой/пагинацией. */
export const fetchFeed = async (
  workspaceSid: string,
  body: FeedRequest,
): Promise<FeedResponse> => {
  if (IS_MOCK) {
    await delay();
    return mockFeedResponse(body);
  }
  const {data} = await http.post<FeedResponse>(
    `/workspaces/${workspaceSid}/communications/feed`,
    body,
  );
  return data;
};

/** Максимальный размер страницы ленты на бэкенде. */
const FEED_PAGE_LIMIT = 100;

/**
 * Лента с добором страниц до `maxRows` (бэкенд ограничивает страницу 100 строками).
 * Используется legacy-обёртками, которым нужен «весь срез» (модалки менеджера и т.п.).
 */
export const fetchFeedPages = async (
  workspaceSid: string,
  body: Omit<FeedRequest, 'skip' | 'limit'>,
  maxRows: number,
): Promise<FeedResponse['rows']> => {
  const rows: FeedResponse['rows'] = [];
  let skip = 0;
  while (rows.length < maxRows) {
    const limit = Math.min(FEED_PAGE_LIMIT, maxRows - rows.length);
    const page = await fetchFeed(workspaceSid, {...body, skip, limit});
    rows.push(...page.rows);
    skip += page.rows.length;
    if (page.rows.length < limit || skip >= page.total) break;
  }
  return rows;
};

/** Минимальный рейтинг workspace с серверной пагинацией и отменой запроса. */
export const fetchWorkspaceRating = async (
  workspaceSid: string,
  body: import('./types').WorkspaceRatingRequest,
  signal?: AbortSignal,
): Promise<import('./types').WorkspaceRatingPage> => {
  const {data} = await http.post<import('./types').WorkspaceRatingPage>(
    `/workspaces/${workspaceSid}/analytics/rating`,
    body,
    {signal},
  );
  return data;
};

/** Агрегаты дашборда за период. */
export const fetchDashboard = async (
  workspaceSid: string,
  body: AnalyticsRequest,
): Promise<DashboardResponse> => {
  const {data} = await http.post<DashboardResponse>(
    `/workspaces/${workspaceSid}/analytics/dashboard`,
    body,
  );
  return data;
};

/** Очереди дашборда (urgent/practices/management). */
export const fetchQueues = async (
  workspaceSid: string,
  body: AnalyticsRequest,
): Promise<QueuesResponse> => {
  const {data} = await http.post<QueuesResponse>(
    `/workspaces/${workspaceSid}/analytics/queues`,
    body,
  );
  return data;
};

/**
 * Длительность коммуникаций за выбранный месяц (тарифные минуты).
 * Только администратор воркспейса (`manage-workspace`) / оператор — остальным
 * бэк отвечает 403.
 */
export const fetchMinutes = async (
  workspaceSid: string,
  body: MinutesRequest,
): Promise<MinutesResponse> => {
  if (IS_MOCK) {
    await delay();
    return mockMinutesResponse(body);
  }
  const {data} = await http.post<MinutesResponse>(
    `/workspaces/${workspaceSid}/analytics/minutes`,
    body,
  );
  return data;
};

/**
 * Блок «Нейроаналитик» карточки менеджера (spec 20): последний анализ
 * смотрящего и доступность обновления. LLM не вызывает.
 */
export const fetchManagerBrief = async (
  workspaceSid: string,
  userSid: string,
): Promise<ManagerBrief> => {
  if (IS_MOCK) {
    await delay();
    return mockManagerBrief(userSid);
  }
  const {data} = await http.get<ManagerBrief>(
    `/workspaces/${workspaceSid}/analytics/manager-brief/${userSid}`,
  );
  return data;
};

/**
 * Получить/обновить анализ по кнопке (LLM, до ~60 с) — раз в сутки.
 * 409 `manager-brief-refresh-locked` — уже обновляли сегодня;
 * 409 `manager-brief-not-enough-data` — мало целевых звонков за 7 дней.
 */
export const generateManagerRecommendation = async (
  workspaceSid: string,
  userSid: string,
): Promise<ManagerBrief> => {
  if (IS_MOCK) {
    await delay(1500);
    return mockGenerateManagerRecommendation(userSid);
  }
  const {data} = await http.post<ManagerBrief>(
    `/workspaces/${workspaceSid}/analytics/manager-brief/${userSid}/recommendation`,
    undefined,
    {timeout: 90_000},
  );
  return data;
};

/** Ядро коммуникации. */
export const getCommunication = async (
  workspaceSid: string,
  sid: string,
): Promise<BackendCommunication> => {
  const {data} = await http.get<BackendCommunication>(
    `/workspaces/${workspaceSid}/communications/${sid}`,
  );
  return data;
};

/** Результат анализа коммуникации (`formResult` + `transcriptText`). */
export const getCommunicationResult = async (
  workspaceSid: string,
  sid: string,
): Promise<CommunicationResultResponse> => {
  const {data} = await http.get<CommunicationResultResponse>(
    `/workspaces/${workspaceSid}/communications/${sid}/result`,
  );
  return data;
};

/** Детальный операционный статус коммуникации. */
export const getCommunicationStatus = async (
  workspaceSid: string,
  sid: string,
): Promise<CommunicationStatusResponse> => {
  const {data} = await http.get<CommunicationStatusResponse>(
    `/workspaces/${workspaceSid}/communications/${sid}/status`,
  );
  return data;
};

/** Перезапуск обработки коммуникации. */
export const startReprocess = async (
  workspaceSid: string,
  communicationId: string,
): Promise<void> => {
  await http.post(
    `/workspaces/${workspaceSid}/communications/reprocess/start`,
    {
      communicationId,
    },
  );
};

// ─── Диагностика приёма: сырой список + scoped-перезапуск ────────────────────

/**
 * Сырой список коммуникаций воркспейса (диагностика приёма). Для админа
 * воркспейса/оператора скоуп = все коммуникации воркспейса (включая
 * несметченные и упавшие). Полные документы — без projection, чтобы не урезать
 * required-поля модели.
 */
export const fetchCommunications = async (
  workspaceSid: string,
  body: CommunicationsListRequest,
): Promise<CommunicationRecord[]> => {
  const {data} = await http.post<CommunicationRecord[]>(
    `/workspaces/${workspaceSid}/communications`,
    body,
  );
  return data;
};

/** Один план в ответе `/result` (плоская форма результата). */
interface PlanResultEntry {
  planKey?: string | null;
  formResult?: Record<string, unknown> | null;
}

/**
 * Достаёт плоскую форму результата анализа из ответа `/result`.
 *
 * Приоритет — верхнеуровневый `formResult`: это **агрегация всех планов** с
 * плоскими ключами, где более новый план перезаписывает одноимённые поля более
 * старого (планов может быть 2, 3, 5+ — всё уже слито бэком в один объект).
 * Адаптер фронта ждёт плоские ключи (`score`, `criteria`, `recommendation`, …),
 * поэтому берём именно его. На всякий случай де-namespace'им dotted-префиксы
 * `<planId>.` (если бэк вдруг отдаст слитые namespaced-ключи) — для плоских
 * ключей это no-op. `results[]` остаётся только фолбэком, когда верхнего
 * `formResult` нет.
 */
const pickPlanFormResult = (
  res: CommunicationResultResponse,
): Record<string, unknown> => {
  // Де-namespace ` <planId>.field` → `field`. Плоские ключи проходят без изменений.
  const deNamespace = (
    form: Record<string, unknown>,
  ): Record<string, unknown> => {
    const flat: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(form)) {
      const dot = k.indexOf('.');
      const key = dot >= 0 ? k.slice(dot + 1) : k;
      if (!(key in flat)) flat[key] = v;
    }
    return flat;
  };

  const root = res.formResult;
  if (root && typeof root === 'object') {
    return deNamespace(root);
  }

  const results = Array.isArray(res.results)
    ? (res.results as PlanResultEntry[])
    : [];
  const chosen =
    results.find((r) => r?.planKey === 'base-call-review') ?? results[0];
  if (chosen?.formResult && typeof chosen.formResult === 'object') {
    return chosen.formResult;
  }
  return {};
};

/**
 * Запись коммуникации как Blob (для аудиоплеера). S3 наружу закрыт — байты
 * проксируются через сервис, поэтому тянем через авторизованный клиент
 * (заголовок `X-Tests-User-Sid`/Gatekeeper), а не прямой ссылкой в `<audio>`.
 */
export const getCommunicationMediaBlob = async (
  workspaceSid: string,
  sid: string,
): Promise<Blob> => {
  const {data} = await http.get<Blob>(
    `/workspaces/${workspaceSid}/communications/${sid}/media`,
    {responseType: 'blob'},
  );
  return data;
};

/** Стадия пайплайна, с которой начать перепрогон коммуникации. */
export type ReprocessFromStage = 'transcription' | 'analysis';

/** Опции перезапуска: стадия рестарта + scope анализа + форсированная транскрибация. */
export interface ReprocessOptions {
  /**
   * Стадия рестарта: `transcription` (полностью) или `analysis` (только анализ —
   * пересматчить планы и прогнать). Имеет приоритет над `forcePreprocess`.
   */
  fromStage?: ReprocessFromStage;
  /** `failed` — только упавшие планы; `matched` — все сметченные планы. */
  analysisScope?: 'failed' | 'matched';
  /** Legacy: прогнать препроцесс (транскрибацию) заново. Предпочитайте `fromStage`. */
  forcePreprocess?: boolean;
}

/** Ответ ручки перезапуска (best-effort: ok + workflowId / error + hint). */
export interface ReprocessResult {
  ok?: boolean;
  workflowId?: string | null;
  runId?: string | null;
  summary?: string | null;
  hint?: string | null;
  error?: string | null;
}

/** Перезапуск обработки с выбором scope (упавшие/полностью). */
export const startReprocessScoped = async (
  workspaceSid: string,
  communicationId: string,
  options: ReprocessOptions = {},
): Promise<ReprocessResult> => {
  const {data} = await http.post<ReprocessResult>(
    `/workspaces/${workspaceSid}/communications/reprocess/start`,
    {communicationId, ...options},
  );
  return data ?? {};
};

// ─── Legacy-обёртки (Communication[] поверх ленты) ──────────────────────────

/** Активный воркспейс из localStorage (синхронно, без jotai). */
const requireWorkspaceSid = (): string => {
  const ws = readActiveWorkspaceId();
  if (!ws) throw new Error('Активный воркспейс не выбран');
  return ws;
};

const fetchAllCommunications = async (): Promise<Communication[]> => {
  const rows = await fetchFeedPages(
    requireWorkspaceSid(),
    {
      planSid: null,
      filter: {},
      sort: [{field: 'occurredAt', direction: 'desc'}],
    },
    500,
  );
  return rows.map(adaptFeedRow);
};

export const meerecClient = {
  async getCommunications(
    filter: CommunicationsFilter = {},
  ): Promise<Communication[]> {
    if (IS_MOCK) {
      await delay();
      let items = [...MOCK_COMMUNICATIONS];
      if (filter.managerId)
        items = items.filter((c) => c.managerId === filter.managerId);
      if (filter.isTargeted !== undefined)
        items = items.filter((c) => c.isTargeted === filter.isTargeted);
      if (filter.hasQueueRecommendation)
        items = items.filter((c) => c.queueItem !== null);
      if (filter.status)
        items = items.filter((c) => c.status === filter.status);
      if (filter.search) {
        const q = filter.search.toLowerCase();
        items = items.filter(
          (c) =>
            c.client.toLowerCase().includes(q) ||
            c.manager.toLowerCase().includes(q) ||
            c.phone.includes(q) ||
            c.id.toLowerCase().includes(q),
        );
      }
      return items;
    }
    // Реальный режим: лента с серверным MQL-фильтром; остаточные фильтры
    // (search) — на клиенте после адаптера.
    const backendFilter: Record<string, unknown> = {};
    if (filter.managerId)
      backendFilter['metadata.userSid'] = {$in: [filter.managerId]};
    if (filter.isTargeted !== undefined)
      backendFilter['result.isTargeted'] = filter.isTargeted;
    if (filter.status)
      backendFilter['result.callStatus'] = CALL_STATUS_TO_API[filter.status];

    const rows = await fetchFeedPages(
      requireWorkspaceSid(),
      {
        planSid: null,
        filter: backendFilter,
        sort: [{field: 'occurredAt', direction: 'desc'}],
      },
      200,
    );
    let items = rows.map(adaptFeedRow);
    if (filter.search) {
      const q = filter.search.toLowerCase();
      items = items.filter(
        (c) =>
          c.client.toLowerCase().includes(q) ||
          c.phone.includes(q) ||
          c.id.toLowerCase().includes(q),
      );
    }
    return items;
  },

  async getCommunication(id: string): Promise<Communication> {
    if (IS_MOCK) {
      await delay();
      if (id === 'C-1841') return MOCK_DETAILED_CALL;
      const found = MOCK_COMMUNICATIONS.find((c) => c.id === id);
      if (!found) throw new Error(`Communication ${id} not found`);
      return found;
    }
    // Карточка = ядро коммуникации + результат анализа (formResult/transcript).
    const ws = requireWorkspaceSid();
    const [core, result] = await Promise.all([
      getCommunication(ws, id),
      getCommunicationResult(ws, id).catch(
        () => ({}) as CommunicationResultResponse,
      ),
    ]);
    const metadata = (core.metadata ?? {}) as Record<string, unknown>;
    const communication = adaptFeedRow({
      communicationId: id,
      occurredAt: core.occurredAt,
      processingStatus: core.processingStatus ?? 'ready',
      userSid:
        typeof metadata['userSid'] === 'string'
          ? (metadata['userSid'] as string)
          : null,
      direction:
        metadata['direction'] === 'inbound'
          ? 'inbound'
          : metadata['direction'] === 'outbound'
            ? 'outbound'
            : null,
      durationSeconds: extractDurationSeconds(metadata),
      hasTranscript: Boolean(result.transcriptText),
      hasMedia: Boolean(core.mediaRef),
      // Плоская форма результата плана (плоские ключи), а не merged namespaced.
      result: pickPlanFormResult(result),
      metadata,
    });
    communication.transcriptText = result.transcriptText ?? null;
    return communication;
  },

  async getQueueItems(): Promise<QueueRecommendation[]> {
    if (IS_MOCK) {
      await delay();
      return MOCK_QUEUE_ITEMS;
    }
    const comms = await fetchAllCommunications();
    return queueFromCommunications(comms);
  },

  async getKpi(period: string): Promise<KpiSnapshot> {
    if (IS_MOCK) {
      await delay(100);
      return MOCK_KPI[period] ?? MOCK_KPI.week;
    }
    const comms = await fetchAllCommunications();
    return kpiFromCommunications(comms, period as Period);
  },

  async getEmployees(): Promise<Employee[]> {
    if (IS_MOCK) {
      await delay();
      return MOCK_EMPLOYEES;
    }
    const comms = await fetchAllCommunications();
    return employeesFromCommunications(comms);
  },

  async getEmployee(id: string): Promise<Employee> {
    if (IS_MOCK) {
      await delay(100);
      const found = MOCK_EMPLOYEES.find((e) => e.id === id);
      if (!found) throw new Error(`Employee ${id} not found`);
      return found;
    }
    const comms = await fetchAllCommunications();
    const employee = employeeFromCommunications(comms, id);
    if (!employee) throw new Error(`Employee ${id} not found`);
    return employee;
  },
};
